import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, runBacktest, type BacktestReport } from '../api/client';
import { savedStrategies, type SavedStrategy } from '../research/content';
import { ResultsView } from '../strategy/ResultsView';

/** Robustness checks rerun the strategy many times; they are optional here. */
const RIGOR_KEYS = ['walk_forward', 'monte_carlo', 'sweep', 'crisis_presets'];

function withoutRigor(spec: Record<string, unknown>): Record<string, unknown> {
  const test = { ...(spec['test'] as Record<string, unknown>) };
  for (const key of RIGOR_KEYS) delete test[key];
  return { ...spec, test };
}

function hasRigor(spec: Record<string, unknown>): boolean {
  const test = spec['test'] as Record<string, unknown> | undefined;
  return RIGOR_KEYS.some((k) => test?.[k] !== undefined);
}

function describe(spec: Record<string, unknown>): string {
  const universe = spec['universe'] as { symbols?: string[] } | undefined;
  const data = spec['data'] as { interval?: string } | undefined;
  const test = spec['test'] as { start?: string; end?: string } | undefined;
  return `${universe?.symbols?.join(', ') ?? ''} · ${data?.interval ?? ''} · ${test?.start ?? ''} to ${test?.end ?? ''}`;
}

function StrategyRow({ strategy }: { strategy: SavedStrategy }) {
  const [rigor, setRigor] = useState(false);
  const [report, setReport] = useState<BacktestReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: () => runBacktest(rigor ? strategy.spec : withoutRigor(strategy.spec)),
    onSuccess: (data) => {
      setReport(data.data);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Backtest failed');
      setReport(null);
    },
  });

  return (
    <div className="card research-card">
      <div className="research-card-head">
        <span className={`badge ${strategy.source === 'Exam' ? 'badge--yellow' : strategy.source === 'Fund' ? 'badge--green' : 'badge--gray'}`}>{strategy.source}</span>
        <strong>{strategy.name}</strong>
      </div>
      <p className="section-description">{describe(strategy.spec)}</p>
      <div className="research-actions">
        <button type="button" className="btn btn--primary" disabled={run.isPending} onClick={() => run.mutate()}>
          {run.isPending ? 'Running… (a few minutes)' : 'Run backtest'}
        </button>
        {hasRigor(strategy.spec) && (
          <label>
            <input type="checkbox" checked={rigor} onChange={(e) => setRigor(e.target.checked)} /> Include
            walk-forward and Monte Carlo (much slower)
          </label>
        )}
      </div>
      <details className="research-details">
        <summary>Strategy definition (DSS)</summary>
        <pre className="research-spec">{JSON.stringify(strategy.spec, null, 2)}</pre>
      </details>
      {error && <div className="error-message">{error}</div>}
      {report && <ResultsView report={report} />}
    </div>
  );
}

export function SavedStrategiesPage() {
  return (
    <div className="page">
      <h2>Saved Strategies</h2>
      <p className="section-description">
        The strategies from the exam work and the trading school. Run one to see every trade, the equity curve and
        the scorecard. They test the practice period only (before the exam lock), and need the forex data loaded
        (Data Import → Import Forex Data).
      </p>
      {savedStrategies.map((s) => (
        <StrategyRow key={`${s.source}-${s.id}`} strategy={s} />
      ))}
    </div>
  );
}
